import { useLingui } from '@lingui/react/macro'
import { useEnvironmentVariables, useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import type { MathNode } from 'features/math'
import { forecastMath } from 'features/math'
import { ChartDataPoint, DataLayer, TimeRange } from 'features/systemLens'
import {
  applyAgentPenSeries,
  getAgentPenSeries,
  subscribeAgentPenSeries,
} from 'features/timeSeries/agentTab/agentPenSeries'
import {
  applyModelSeries,
  getHideExcludedDays,
  subscribeHideExcluded,
} from 'features/timeSeries/agentTab/modelSeries'
import {
  computedPenId,
  type ComputedTrendSeries,
} from 'features/timeSeries/computed/computedTrendStore'
import {
  perfBeginFetch,
  perfCount,
  perfFetchDone,
  perfMark,
} from 'features/timeSeries/perf/processDataPerf'
import {
  performMultivariateLinearRegression,
  performRegression,
  RegressionResult,
} from 'features/timeSeries/regressionHelpers'
import {
  addThresholdDataToChartPoints,
  getEnabledThresholds,
} from 'features/timeSeries/services/thresholdService'
import {
  isAbortError,
  TimeSeriesRequest,
  TimeSeriesResponse,
  TimeSeriesService,
} from 'features/timeSeries/timeSeriesService'
import { useTimeSeriesDataCache } from 'features/timeSeries/useTimeSeriesDataCache'
import { computedScalePairsFor } from 'features/timeSeries/utils/computedScalePairs'
import {
  applyDerivedValues,
  applySmoothingConfigs,
  applyThresholdFilters,
  collectFilteredPoints,
  FilteredPointsSeries,
  mergeComputedSeriesRows,
  sanitizeSensorId,
} from 'features/timeSeries/utils/dataProcessing'
import { isUserDerivedTag } from 'features/timeSeries/utils/derivedTags'
import {
  buildLoadSettledKey,
  setSettledLoadKey,
} from 'features/timeSeries/utils/loadSettledSignal'
import {
  mergeTimeSeriesResponse,
  toAscendingChartRows,
} from 'features/timeSeries/utils/mergeTimeSeriesResponse'
import { isSteppedSensor } from 'features/timeSeries/utils/steppedPens'
import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import {
  BaseSensorEntry,
  ChartTimeUnit,
  DateMode,
  EnhancedSensorEntry,
  LocalThresholdOverrideType,
  RegressionConfig,
  ResampleConfig,
  RolodexEntryType,
  SmoothingConfig,
  ThresholdFilterConfig,
  TimeSeriesDerivedValueEntryJSON,
} from 'types'

import { useDateMode } from './useDateMode'
import { mergeBucketColumns, usePenBuckets } from './usePenBuckets'

const EMPTY_COMPUTED_SERIES: ComputedTrendSeries[] = []

interface UseTimeSeriesDataProcessingProps {
  sensorEntries: BaseSensorEntry[]
  selectedSensors: BaseSensorEntry[]
  setSelectedSensors: Dispatch<SetStateAction<BaseSensorEntry[]>>
  timeRange: TimeRange
  setTimeRange: Dispatch<SetStateAction<TimeRange>>
  dataLayers: DataLayer[]
  siteID: string
  orgID: string
  derivedValueIdsToDeleteRef: MutableRefObject<Set<string>>
  relativeStart: { value?: number; unit: ChartTimeUnit }
  relativeEnd: { isNow: boolean; value?: number; unit: ChartTimeUnit }
  initialDateMode?: DateMode
  computedSeries?: ComputedTrendSeries[]
}

/**
 * A live-tail window is nominally constant-length, but bucket snapping can shift
 * each end independently by up to one bucket, so the measured length wobbles
 * slightly between polls. One minute absorbs that without being loose enough to
 * mistake a user's zoom for a poll.
 */
const LIVE_TAIL_RANGE_TOLERANCE_MS = 60_000

export const useTimeSeriesDataProcessing = ({
  sensorEntries,
  selectedSensors,
  setSelectedSensors,
  timeRange,
  setTimeRange,
  dataLayers,
  siteID,
  orgID,
  derivedValueIdsToDeleteRef,
  relativeStart,
  relativeEnd,
  initialDateMode,
  computedSeries = EMPTY_COMPUTED_SERIES,
}: UseTimeSeriesDataProcessingProps) => {
  const { t } = useLingui()

  const {
    parse,
    isFunctionNode,
    isOperatorNode,
    isParenthesisNode,
    isSymbolNode,
  } = forecastMath
  const [chartData, setChartData] = useState<ChartDataPoint[]>([])
  const [rawChartData, setRawChartData] = useState<ChartDataPoint[]>([])
  const [filteredPoints, setFilteredPoints] = useState<FilteredPointsSeries[]>(
    []
  )

  /**
   * Whether anything is currently drawn.
   *
   * A ref rather than reading `rawChartData` directly: this is consulted inside
   * `fetchRawData`, and taking the state value would put it in that callback's
   * dependencies and re-create it every time data lands.
   */
  const hasRenderedDataRef = useRef(false)
  const { IS_ADNOC_ENVIRONMENT } = useEnvironmentVariables()
  const [interpolatePreference, setInterpolate] = useState(false)
  const interpolate = !IS_ADNOC_ENVIRONMENT && interpolatePreference
  const [isProcessingData, setIsProcessingData] = useState(false)
  const [localThresholdOverrides, setLocalThresholdOverrides] =
    useState<LocalThresholdOverrideType>({})
  const { authorization, authorizedAxios } = useGeckoAuth()
  const { TIME_SERIES_SERVICE } = useRuntimeVariables()
  const { rolodexPatch } = useRolodexEdit()

  // Ref shared with useDateMode so poll ticks can be skipped while fetching
  const isLoadingRef = useRef(false)

  // Flags the source of the next fetchRawData invocation. Zoom keeps the
  // previous chartData visible as a placeholder (so the corner spinner overlay
  // reads); every other trigger clears stale data so the centered loader shows.
  // handleZoom in useUPlotGenerators flips this to 'zoom' just before updating
  // timeRange.
  const loadTriggerRef = useRef<'zoom' | 'other'>('other')

  // Remembered fetch inputs so we can detect live-tail polls and preserve the
  // existing chart through them. Both ends of a live-tail window slide, so a
  // poll is identified by an unchanged pen set over a same-length window that
  // has not moved backwards — not by an unchanged start.
  const prevFetchSensorsKeyRef = useRef<string>('')
  const prevFetchFromRef = useRef<string>('')
  const prevFetchFromMsRef = useRef<number>(0)
  const prevFetchRangeMsRef = useRef<number>(0)
  const latestFetchSignatureRef = useRef<string>('')
  const hasFetchedOnceRef = useRef(false)

  const {
    dateMode,
    setDateMode,
    exitLiveTail,
    liveTail,
    setLiveTail,
    cadenceSeconds,
    setCadenceSeconds,
  } = useDateMode({
    setTimeRange,
    timeRange,
    relativeStart,
    relativeEnd,
    initialDateMode,
    isLoadingRef,
  })

  // Data processing state
  const [thresholdFilters, setThresholdFilters] = useState<
    Record<string, ThresholdFilterConfig>
  >({})
  const [smoothingConfigs, setSmoothingConfigs] = useState<
    Record<string, SmoothingConfig>
  >({})
  const [resampleConfigs, setResampleConfigs] = useState<
    Record<string, ResampleConfig>
  >({})
  const [derivedValues, internalSetDerivedValues] = useState<
    TimeSeriesDerivedValueEntryJSON[]
  >([])
  const [regressionConfigs, setRegressionConfigs] = useState<
    RegressionConfig[]
  >([])
  const [regressionResults, setRegressionResults] = useState<
    Record<string, RegressionResult>
  >({})
  const [sensorVisibilityConfig, setSensorVisibilityConfig] = useState<
    Record<string, boolean>
  >({})
  const [customSensorColors, setCustomSensorColors] = useState<
    Record<string, string>
  >({})

  const penBuckets = usePenBuckets(resampleConfigs, timeRange)

  const setDerivedValues: Dispatch<
    SetStateAction<TimeSeriesDerivedValueEntryJSON[]>
  > = useCallback(
    (updater) => {
      let newDerivedValues: TimeSeriesDerivedValueEntryJSON[] = []
      internalSetDerivedValues((prev) => {
        newDerivedValues =
          typeof updater === 'function' ? updater(prev) : updater
        return newDerivedValues
      })
      setSelectedSensors((prev) => {
        const selectedSensorIds = new Set(prev.map((s) => s.id))
        newDerivedValues.forEach((dv) => {
          dv.data.dependencyIds.forEach((sensorId) => {
            if (!selectedSensorIds.has(sensorId)) {
              selectedSensorIds.add(sensorId)
            }
          })
        })
        return Array.from(selectedSensorIds)
          .map((id) => sensorEntries.find((s) => s.id === id))
          .filter((s) => s !== undefined)
      })
      setSensorVisibilityConfig((prev) => {
        const newConfig = { ...prev }
        newDerivedValues.forEach((dv) => {
          if (isUserDerivedTag(dv)) return
          const sensorId = dv.data.dependencyIds?.[0]
          if (!sensorId || !sensorEntries.find((s) => s.id === sensorId)) return
          newConfig[sensorId] = false
        })
        return newConfig
      })
    },
    [setSelectedSensors, sensorEntries]
  )

  // Initialize data fetching hook
  const {
    fetchData: fetchTimeSeriesData,
    prefetchData: prefetchTimeSeriesData,
    getRequestStatus,
    isLoading: isLoadingData,
  } = useTimeSeriesDataCache({
    debounceMs: 500,
    // The aggregate endpoint reports bucket starts, so at the 7-day default
    // every timestamp on this page snapped to :00. Fetch the real sample times
    // for the pens where that is affordable.
    exactTimestamps: true,
  })

  // Keep the loading ref in sync so useDateMode can read it synchronously
  useEffect(() => {
    isLoadingRef.current = isLoadingData
  }, [isLoadingData])

  // Enhanced sensor entries with local threshold overrides
  const enhancedSensorEntries: EnhancedSensorEntry[] = useMemo(() => {
    return sensorEntries.map((sensor) => {
      const override = localThresholdOverrides[sensor.id]
      if (!override) return sensor

      return {
        ...sensor,
        data: {
          ...sensor.data,
          thresholds: override,
        },
      }
    })
  }, [sensorEntries, localThresholdOverrides])

  // Get selected sensor entries for threshold calculations
  const selectedSensorEntries = useMemo(
    () =>
      enhancedSensorEntries.filter((sensor) =>
        selectedSensors.find(
          (selectedSensor) => selectedSensor.id === sensor.id
        )
      ),
    [enhancedSensorEntries, selectedSensors]
  )

  // Calculate enabled thresholds
  // The agent card's "hide days the model excluded" switch lives in a module
  // store (the chart, the card and the merge step all need it). Subscribing here
  // is what turns flipping it into a re-process.
  const hideExcludedDays = useSyncExternalStore(
    subscribeHideExcluded,
    getHideExcludedDays,
    getHideExcludedDays
  )
  // The agent's rolling-average pens, for the same reason: their values are
  // computed here, so adding one has to re-process.
  const agentPenSeries = useSyncExternalStore(
    subscribeAgentPenSeries,
    getAgentPenSeries,
    getAgentPenSeries
  )

  const enabledThresholds = useMemo(() => {
    return getEnabledThresholds(selectedSensorEntries)
  }, [selectedSensorEntries])

  const hasEnabledSensorLayer = useCallback(
    () => dataLayers.some((layer) => layer.type === 'sensor' && layer.enabled),
    [dataLayers]
  )

  const buildTimeSeriesRequest = useCallback(
    (range: TimeRange): TimeSeriesRequest => {
      let interval: number
      let interval_unit: ChartTimeUnit

      if (range.interval && range.intervalUnit) {
        interval = range.interval
        interval_unit = range.intervalUnit
      } else {
        const timeRangeSeconds =
          (new Date(range.to).getTime() - new Date(range.from).getTime()) / 1000
        // Share the bucket budget across pens: every point past the client's
        // resample cap is fetched and merged but never drawn, so a 15-pen
        // chart should not ship 15x the buckets a 1-pen chart does. See
        // BUCKETED_FETCH_BUDGET in config/constants.ts. Every range-setting
        // path bakes its interval with the same helper and pen count, so this
        // fallback and a later interval backfill always agree.
        const calculated = TimeSeriesService.getIntervalForPenCount(
          timeRangeSeconds,
          selectedSensors.length
        )
        interval = calculated.interval
        interval_unit = calculated.interval_unit
      }

      const steppedEntryIds = selectedSensors
        .filter((sensor) => isSteppedSensor(sensor.data))
        .map((sensor) => sensor.id)

      return {
        entry_ids: selectedSensors.map((sensor) => sensor.id),
        start_datetime: range.from,
        end_datetime: range.to,
        interval,
        interval_unit,
        interpolate,
        ...(steppedEntryIds.length > 0
          ? { stepped_entry_ids: steppedEntryIds }
          : {}),
      }
    },
    [interpolate, selectedSensors]
  )

  const prefetchTimeRange = useCallback(
    async (range: TimeRange): Promise<void> => {
      if (
        !authorization ||
        liveTail ||
        !hasEnabledSensorLayer() ||
        selectedSensors.length === 0
      ) {
        return
      }

      await prefetchTimeSeriesData(buildTimeSeriesRequest(range))
    },
    [
      authorization,
      buildTimeSeriesRequest,
      hasEnabledSensorLayer,
      liveTail,
      prefetchTimeSeriesData,
      selectedSensors.length,
    ]
  )

  // Fetch raw data only when necessary (sensors, time range, etc. change)
  const fetchRawData = useCallback(
    async (options?: { bypassDebounce?: boolean; immediate?: boolean }) => {
      if (!authorization) {
        return
      }

      // Identity of the load this invocation represents: the current pen set over
      // the current window. Published to the empty-state gate so it can tell a
      // genuine "settled with no data" from a transient loading gap (see
      // loadSettledSignal). Computed from the same inputs the consumer holds, so
      // the keys match on both sides.
      const loadSettledKey = buildLoadSettledKey(
        selectedSensors.map((sensor) => sensor.id),
        timeRange.from,
        timeRange.to
      )

      if (!hasEnabledSensorLayer() || selectedSensors.length === 0) {
        setRawChartData([])
        setChartData([])
        // No configured pens is a legitimate empty outcome, not a pending load.
        setSettledLoadKey(loadSettledKey)
        return
      }

      const fetchParams = buildTimeSeriesRequest(timeRange)
      const requestStatus = getRequestStatus(fetchParams)

      const sensorsKey = selectedSensors
        .map((sensor) => sensor.id)
        .sort()
        .join(',')

      // A live-tail poll slides BOTH ends of the window forward by the same
      // amount — the start is `now - lookback`, so it moves with `now`. The
      // previous check required `from` to be unchanged, which never holds, so
      // this branch was unreachable and every poll tore the chart down.
      // Identify a poll by what is actually invariant across one: the same pens
      // over a same-length window that has not moved backwards.
      const rangeMs =
        new Date(timeRange.to).getTime() - new Date(timeRange.from).getTime()
      const fromMs = new Date(timeRange.from).getTime()
      const prevRangeMs = prevFetchRangeMsRef.current
      const isSameLengthWindow =
        prevRangeMs > 0 &&
        Math.abs(rangeMs - prevRangeMs) <= LIVE_TAIL_RANGE_TOLERANCE_MS
      const isLiveTailPoll =
        liveTail &&
        prevFetchSensorsKeyRef.current === sensorsKey &&
        isSameLengthWindow &&
        fromMs >= prevFetchFromMsRef.current

      const isZoomLoad = loadTriggerRef.current === 'zoom'

      // Keep whatever is already drawn. Only a genuinely cold chart — nothing on
      // screen to keep — earns the centered loader.
      //
      // This used to preserve for zoom and cache hits but not pan, because only
      // the two zoom paths set `loadTriggerRef`. So every pan blanked the chart
      // and then transitioned into it, which reads as a reload rather than a
      // move and defeats the point of the transition. What matters is not how
      // the range changed but whether there is anything worth keeping.
      //
      // Safe to hold the previous window: a fetch that genuinely fails still
      // clears below, so a stale window cannot linger silently, and the corner
      // spinner (gated on hasDataPoints) says a load is in flight.
      const shouldPreserveData =
        hasRenderedDataRef.current ||
        isZoomLoad ||
        isLiveTailPoll ||
        requestStatus === 'cached' ||
        requestStatus === 'pending' ||
        requestStatus === 'partial'
      loadTriggerRef.current = 'other'
      prevFetchSensorsKeyRef.current = sensorsKey
      prevFetchFromRef.current = timeRange.from
      prevFetchFromMsRef.current = fromMs
      prevFetchRangeMsRef.current = rangeMs

      // Preserve the chart when the next request is already available or in
      // flight; otherwise surface the centered loader for genuinely cold loads.
      //
      // This clear used to be wrapped in flushSync, to stop a fast fetch from
      // merging the clear and the repopulate into one render: that would leave
      // hasDataPoints true, skip the uPlot teardown/recreate, and strand alert
      // positions on the previous x-scale. The teardown is no longer what
      // re-positions anything — the chart moves its scales in place and
      // publishes the move (see chartViewportSignal), and overlays subscribe to
      // that rather than to the instance's lifecycle. So a merged render is now
      // the good case, not a hazard: the chart transitions straight to the new
      // window without a synchronous re-render forcing a blank frame first.
      if (!shouldPreserveData) {
        perfCount('chartCleared')
        setRawChartData([])
        setChartData([])
      }

      // A load is now in flight for this window. Retract any prior settle so the
      // empty-state gate stays closed (loader shown, not "No data") until THIS
      // load reaches a real terminal outcome below — never on the loading blip a
      // superseded fetch leaves behind.
      setSettledLoadKey(null)

      setIsProcessingData(true)

      const perfSeq = perfBeginFetch({
        trigger: isZoomLoad
          ? 'zoom'
          : isLiveTailPoll
            ? 'live-tail'
            : liveTail
              ? 'live-tail'
              : 'other',
        cacheStatus: requestStatus,
        sensorCount: fetchParams.entry_ids.length,
        rangeSeconds:
          (new Date(timeRange.to).getTime() -
            new Date(timeRange.from).getTime()) /
          1000,
        interval: fetchParams.interval,
        intervalUnit: fetchParams.interval_unit,
      })

      // The debounce coalesces slider drags; the first load of the page has
      // nothing to coalesce against, so it should not pay the delay.
      const isFirstFetch = !hasFetchedOnceRef.current
      hasFetchedOnceRef.current = true

      const requestSignature = JSON.stringify(fetchParams)
      latestFetchSignatureRef.current = requestSignature

      // Response -> chart rows, shared by the final render and the two-phase
      // provisional paint so both go through identical threshold decoration
      // and the ascending-x guarantee uPlot depends on.
      const responseToChartRows = (response: TimeSeriesResponse) => {
        const allDataPoints = mergeTimeSeriesResponse(response)

        if (dataLayers.find((l) => l.id === 'thresholds')?.enabled) {
          addThresholdDataToChartPoints(allDataPoints, enabledThresholds)
        }

        // uPlot requires strictly ascending x and silently renders garbage
        // otherwise, so guarantee the ordering here rather than inheriting it
        // from the server's sort.
        return toAscendingChartRows(allDataPoints)
      }

      // Coarse-then-refine for large windows: the cache layer paints a fast
      // provisional pass (a cached superset's slice, else a coarse fetch) and
      // then the awaited fetch below lands at full resolution and replaces it.
      // A provisional paint is NOT a settled load: the settled key stays
      // retracted so the empty-state gate keeps waiting on the refine. Live-
      // tail polls are small sliding refreshes and skip the machinery.
      const twoPhase = isLiveTailPoll
        ? undefined
        : {
            onProvisional: (response: TimeSeriesResponse) => {
              // Same staleness guard as the final render below: only the
              // latest window may paint.
              if (latestFetchSignatureRef.current !== requestSignature) return

              const provisionalRows = responseToChartRows(response)
              // An empty provisional adds nothing over the loader, and
              // painting it would clear a preserved previous window.
              if (provisionalRows.length === 0) return

              perfMark('firstDataReady')
              setRawChartData(provisionalRows)
            },
          }

      try {
        const response = await fetchTimeSeriesData(fetchParams, {
          ...options,
          immediate: isFirstFetch || options?.immediate,
          perfSeq,
          twoPhase,
        })

        if (latestFetchSignatureRef.current !== requestSignature) {
          perfFetchDone(perfSeq, { outcome: 'aborted' })
          return
        }

        const rawData = responseToChartRows(response)
        perfFetchDone(perfSeq, {
          outcome: 'success',
          pointCount: rawData.length,
        })
        perfMark('firstDataReady')
        setRawChartData(rawData)
        // Genuine terminal success for the current window (the signature guard
        // above already excluded aborts). Empty `rawData` here is a real
        // "no data in range", which the gate may now trust.
        setSettledLoadKey(loadSettledKey)
      } catch (error) {
        // One definition of "abort" across the whole fetch path. A narrower
        // check here let an axios CanceledError ("signal is aborted without
        // reason") read as a real failure, which published a settle-empty and
        // flashed "No data" for a few frames before the real data landed.
        const isCancellation = isAbortError(error)
        perfFetchDone(perfSeq, {
          outcome: isCancellation ? 'aborted' : 'error',
          error: error instanceof Error ? error.message : String(error),
        })
        if (!isCancellation) {
          console.error('Failed to fetch time series data:', error)

          // A cancellation means a newer request is already on its way, so the
          // preserved chart is a placeholder that request will replace. A real
          // failure has no such successor: if we are still the latest request,
          // nothing else will ever repaint, and `shouldPreserveData` (set for
          // every zoom) would otherwise leave the PREVIOUS window's points
          // stranded under the new `timeRange` indefinitely. uPlot auto-ranges x
          // to the data, so the axis would silently disagree with the range the
          // user asked for. An empty chart is wrong; a chart confidently showing
          // the wrong window is worse.
          if (latestFetchSignatureRef.current === requestSignature) {
            setRawChartData([])
            setChartData([])
            // A real failure is terminal — no successor request will repaint —
            // so the gate must be allowed to surface the empty state rather than
            // spin forever. Aborts are handled above and deliberately skip this.
            setSettledLoadKey(loadSettledKey)
          }
        }
      } finally {
        setIsProcessingData(false)
      }
    },
    [
      dataLayers,
      selectedSensors,
      timeRange,
      fetchTimeSeriesData,
      buildTimeSeriesRequest,
      enabledThresholds,
      liveTail,
      authorization,
      getRequestStatus,
      hasEnabledSensorLayer,
    ]
  )

  useEffect(() => {
    hasRenderedDataRef.current = rawChartData.length > 0
  }, [rawChartData])

  // Kept undefined while no computed series exist so processData does not
  // re-run on window changes alone.
  const computedMergeWindow = useMemo(() => {
    if (computedSeries.length === 0) return undefined
    const fromMs = Date.parse(timeRange.from)
    const toMs = Date.parse(timeRange.to)
    return Number.isFinite(fromMs) && Number.isFinite(toMs)
      ? { fromMs, toMs }
      : undefined
  }, [computedSeries, timeRange])

  // Process raw data whenever processing parameters change
  const processData = useCallback(() => {
    if (rawChartData.length === 0 && computedSeries.length === 0) {
      setChartData([])
      setFilteredPoints([])
      return
    }

    // Apply all transformations in the correct order
    let processedData = [...rawChartData]

    // 1. Apply derived values
    processedData = applyDerivedValues(processedData, derivedValues)
    processedData = mergeComputedSeriesRows(
      processedData,
      computedSeries,
      computedMergeWindow
    )

    // 1b. An agent model's SAVED output (expected, residual) is data, not a
    // formula, so it is merged after the formulas and wins the column.
    processedData = applyModelSeries(processedData, hideExcludedDays)

    // 1c. The agent's rolling-average pens. After the formulas and the model
    // output so an average can be taken of either.
    processedData = applyAgentPenSeries(processedData, agentPenSeries)

    // 2. Apply thresholding. Marks are collected first so they carry the value
    // each point had before it was excluded.
    setFilteredPoints(collectFilteredPoints(processedData, thresholdFilters))
    processedData = applyThresholdFilters(processedData, thresholdFilters)

    // 3. Apply smoothing
    processedData = applySmoothingConfigs(processedData, smoothingConfigs)

    // 4. Attach bucket views, which can add rows on their own grid
    processedData = mergeBucketColumns(processedData, penBuckets)

    setChartData(processedData)
  }, [
    rawChartData,
    derivedValues,
    computedSeries,
    computedMergeWindow,
    thresholdFilters,
    smoothingConfigs,
    penBuckets,
    hideExcludedDays,
    agentPenSeries,
  ])

  // Memoize regression calculation logic
  const calculateRegression = useCallback(
    async (config: RegressionConfig) => {
      if (!config.enabled || !chartData.length) {
        // Clear result if disabled
        if (!config.enabled && regressionResults[config.id]) {
          setRegressionResults((prev) => {
            const newResults = { ...prev }
            delete newResults[config.id]
            return newResults
          })

          // Also remove the associated derived value
          const derivedValueId = `regression_predicted_${config.id}`
          setDerivedValues((prev) =>
            prev.filter((dv) => dv.id !== derivedValueId)
          )
        }
        return
      }

      // Calculate if we should calculate OR if we don't have a result yet
      if (!config.shouldCalculate && regressionResults[config.id]) {
        return
      }

      try {
        // Handle linear regression with sensor variables (including time + sensors)
        const hasNonTimeVariables = config.xVariables?.some(
          (id) => id !== 'timestamp'
        )
        if (config.regressionType === 'linear' && hasNonTimeVariables) {
          // Linear regression with sensor(s) as independent variable(s)
          const independentSensorIds = config.xVariables
          const dependentSensorId = config.yVariable

          // Filter to get only valid data points where all sensors have values
          const validData = chartData.filter((d) => {
            if (typeof d[dependentSensorId] !== 'number') return false
            return independentSensorIds.every((id) => typeof d[id] === 'number')
          })

          if (validData.length < independentSensorIds.length + 1) {
            console.error('Insufficient data points for regression')
            return
          }

          // Prepare features (independent variables) and values (dependent variable)
          const features = validData.map((d) =>
            independentSensorIds.map((id) =>
              id === 'timestamp'
                ? d[id] - validData[0].timestamp
                : (d[id] as number)
            )
          )

          const values = validData.map((d) => d[dependentSensorId] as number)

          // Perform regression
          const result = await performMultivariateLinearRegression(
            features,
            values,
            TIME_SERIES_SERVICE,
            authorizedAxios
          )

          // Add feature names to the result
          const featureNames = independentSensorIds.map((id) => {
            if (id === 'timestamp') {
              return t`Time`
            }
            const sensor = sensorEntries.find((e) => e.id === id)
            const derived = derivedValues.find((dv) => dv.id === id)
            return sensor
              ? (sensor.data?.name as string) || id
              : derived?.data.name || id
          })

          setRegressionResults((prev) => ({
            ...prev,
            [config.id]: {
              ...result,
              featureNames,
              predictions: [], // No time-based predictions for sensor-based regression
            },
          }))

          // Create a derived value for the regression prediction
          const derivedValueId = `regression_predicted_${config.id}`

          // Get the dependent variable name
          const dependentSensor = sensorEntries.find(
            (e) => e.id === config.yVariable
          )
          const dependentName = dependentSensor
            ? dependentSensor.data?.name || config.yVariable
            : config.yVariable

          const derivedValueName = `Predicted ${dependentName}`

          // Build the expression using the regression coefficients
          // For multivariate linear regression: y = β₀ + β₁x₁ + β₂x₂ + ... + βₙxₙ
          const expressionParts: string[] = []

          // Add intercept (β₀)
          expressionParts.push(result.coefficients[0].toString())

          // Add terms for each feature
          independentSensorIds.reverse().forEach((sensorId, index) => {
            const coefficient = result.coefficients[index + 1]
            if (coefficient !== 0) {
              const variableName = sensorId
                .replaceAll('-', '_')
                .replaceAll('.', '_')
              const term =
                coefficient >= 0
                  ? `+ ${coefficient} * ${variableName}`
                  : `- ${Math.abs(coefficient)} * ${variableName}`
              expressionParts.push(term)
            }
          })

          const expression = expressionParts.join(' ')
          // Update or create the derived value
          setDerivedValues((prev) => {
            // Remove any existing regression prediction for this config
            const filtered = prev.filter((dv) => dv.id !== derivedValueId)

            // Add the new derived value
            return [
              ...filtered,
              {
                id: derivedValueId,
                type: RolodexEntryType.TIME_SERIES_DERIVED_VALUE,
                data: {
                  name: derivedValueName,
                  expression,
                  description: t`Multivariate linear regression prediction: ${{ value816_79: result.equation }}`,
                  unit: undefined,
                  dependencyIds: independentSensorIds,
                },
                tags: {},
                updatedAt: undefined,
                createdAt: undefined,
                lastUpdatedBy: undefined,
              },
            ]
          })
        } else {
          // Filter to get only valid timestamp-value pairs
          const validData = chartData
            .filter(
              (d) => d.timestamp > 0 && typeof d[config.yVariable] === 'number'
            )
            .map((d) => ({
              timestamp: d.timestamp as number,
              value: d[config.yVariable] as number,
            }))

          const timestamps = validData.map((d) => d.timestamp)
          const values = validData.map((d) => d.value)

          if (validData.length === 0) {
            return
          }

          const result = await performRegression(
            timestamps,
            values,
            config,
            TIME_SERIES_SERVICE,
            authorizedAxios
          )

          setRegressionResults((prev) => ({
            ...prev,
            [config.id]: result,
          }))
        }

        // Reset shouldCalculate flag after calculation
        if (config.shouldCalculate) {
          setRegressionConfigs((prev) =>
            prev.map((c) =>
              c.id === config.id ? { ...c, shouldCalculate: false } : c
            )
          )
        }
      } catch (error) {
        console.error('Regression calculation failed:', error)
        setRegressionResults((prev) => {
          const newResults = { ...prev }
          delete newResults[config.id]
          return newResults
        })
      }
    },
    [
      chartData,
      sensorEntries,
      derivedValues,
      regressionResults,
      authorizedAxios,
      TIME_SERIES_SERVICE,
      setDerivedValues,
      t,
    ]
  )

  // Serialize enabledThresholds for stable comparison
  const enabledThresholdsKey = useMemo(
    () =>
      JSON.stringify(
        enabledThresholds.map((t) => ({
          sensorId: t.sensorId,
          ll: t.lowerLowerThreshold,
          l: t.lowerThreshold,
          u: t.upperThreshold,
          uu: t.upperUpperThreshold,
        }))
      ),
    [enabledThresholds]
  )

  // Fetch raw data when key dependencies change
  useEffect(() => {
    let cancelled = false

    queueMicrotask(() => {
      if (cancelled) return
      fetchRawData()
    })

    return () => {
      cancelled = true
    }
  }, [
    selectedSensors,
    timeRange.from,
    timeRange.to,
    timeRange.interval,
    timeRange.intervalUnit,
    interpolate,
    dataLayers,
    enabledThresholdsKey,
    liveTail,
    fetchRawData,
  ])

  // Process data whenever raw data or processing parameters change
  useEffect(() => {
    processData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawChartData, derivedValues, thresholdFilters, smoothingConfigs])

  // Handle regression calculations
  useEffect(() => {
    // Only process configs that need calculation
    regressionConfigs.forEach((config) => {
      // Skip if already calculated and shouldCalculate is false
      if (
        !config.shouldCalculate &&
        regressionResults[config.id] &&
        config.enabled
      ) {
        return
      }
      calculateRegression(config)
    })
  }, [regressionConfigs, calculateRegression, regressionResults])

  const unitMapping = useMemo(() => {
    const units: Record<string, string> = {}
    sensorEntries?.forEach((entry) => {
      const unit = entry.data.unit
      if (entry.data.name) {
        // Use 'No Unit' for sensors without a unit
        units[entry.data.name] = unit || t`No Unit`
      }
    })
    return units
  }, [sensorEntries, t])

  const handleRemoveDerivedValue = useCallback(
    async (id: string) => {
      // Find all derived values that need to be removed (including cascading dependents)
      const idsToRemove = new Set<string>([id])

      const findDependents = (targetId: string) => {
        derivedValues.forEach((dv) => {
          if (
            !idsToRemove.has(dv.id) &&
            dv.data.dependencyIds?.includes(targetId)
          ) {
            idsToRemove.add(dv.id)
            // Recursively check if other derived values depend on this one
            findDependents(dv.id)
          }
        })
      }

      findDependents(id)

      // Add to list of IDs to delete on save
      idsToRemove.forEach((idToRemove) => {
        derivedValueIdsToDeleteRef.current.add(idToRemove)
      })

      // Remove from local state - actual deletion happens on chart save
      const updatedDerivedValues = derivedValues.filter(
        (dv) => !idsToRemove.has(dv.id)
      )
      setDerivedValues(updatedDerivedValues)
    },
    [derivedValues, setDerivedValues, derivedValueIdsToDeleteRef]
  )

  const handleChangeColor = useCallback((sensorId: string, color: string) => {
    setCustomSensorColors((prev) => ({
      ...prev,
      [sensorId]: color,
    }))
  }, [])

  const createDerivedValues = useCallback(
    async (
      newDerived: TimeSeriesDerivedValueEntryJSON,
      editingDerivedId: string | null
    ) => {
      if (!newDerived.data.name || !newDerived.data.expression) {
        return derivedValues
      }

      // Parse and validate the expression using math.js
      const parsedExpression = parse(newDerived.data.expression)
      let variables: string[] = []

      const extractVariables = (node: MathNode): void => {
        if (isSymbolNode(node)) {
          variables.push(node.name)
        }
        if ((isFunctionNode(node) || isOperatorNode(node)) && node.args) {
          node.args.forEach(extractVariables)
        }
        if (isParenthesisNode(node)) {
          extractVariables(node.content)
        }
      }
      extractVariables(parsedExpression)
      // Remove duplicates
      variables = [...new Set(variables)]

      // Build the list of dependency IDs from the variables
      const dependencyIds: string[] = []
      variables.forEach((varName) => {
        const derivedVar = derivedValues.find(
          (dv) => sanitizeSensorId(dv.id) === varName
        )
        if (derivedVar) {
          dependencyIds.push(derivedVar.id)
        } else {
          const sensor = sensorEntries.find(
            (e) => sanitizeSensorId(e.id) === varName
          )
          if (sensor) {
            dependencyIds.push(sensor.id)
          }
        }
      })

      const derivedValue: TimeSeriesDerivedValueEntryJSON = {
        id: editingDerivedId || `derived_${Date.now()}`,
        type: RolodexEntryType.TIME_SERIES_DERIVED_VALUE,
        data: {
          name: newDerived.data.name,
          expression: newDerived.data.expression,
          unit: newDerived.data.unit,
          description: newDerived.data.description,
          dependencyIds,
        },
        tags: {
          site: siteID,
        },
      }

      await rolodexPatch([
        {
          id: derivedValue.id,
          type: derivedValue.type,
          data: {
            name: derivedValue.data.name,
            expression: derivedValue.data.expression,
            unit: derivedValue.data.unit,
            description: derivedValue.data.description,
            dependency_ids: dependencyIds,
          },
          tags: {
            site: siteID,
            organization: orgID,
          },
        },
      ])

      if (editingDerivedId) {
        return derivedValues.map((dv) =>
          dv.id === editingDerivedId ? derivedValue : dv
        )
      } else {
        return [...derivedValues, derivedValue]
      }
    },
    [
      siteID,
      orgID,
      rolodexPatch,
      derivedValues,
      sensorEntries,
      parse,
      isFunctionNode,
      isOperatorNode,
      isParenthesisNode,
      isSymbolNode,
    ]
  )

  // Combined loading state for UI
  const isLoading = isLoadingData || isProcessingData

  const computedPenDefs = useMemo(
    (): TimeSeriesDerivedValueEntryJSON[] =>
      computedSeries.map((series) => ({
        id: computedPenId(series.entryId),
        type: RolodexEntryType.TIME_SERIES_DERIVED_VALUE,
        tags: {},
        data: {
          name: series.label,
          expression: '',
          unit: series.units ?? null,
          description: null,
          dependencyIds: [],
        },
      })),
    [computedSeries]
  )
  const computedScalePairs = useMemo(
    () => computedScalePairsFor(computedSeries, sensorEntries ?? []),
    [computedSeries, sensorEntries]
  )

  const derivedValuesWithComputed = useMemo(
    () =>
      computedPenDefs.length
        ? [...derivedValues, ...computedPenDefs]
        : derivedValues,
    [derivedValues, computedPenDefs]
  )

  return {
    // Data state
    chartData,
    setChartData,
    rawChartData,
    interpolate,
    setInterpolate,
    isLoading,
    liveTail,
    setLiveTail,

    // Processing state
    thresholdFilters,
    setThresholdFilters,
    filteredPoints,
    resampleConfigs,
    setResampleConfigs,
    smoothingConfigs,
    setSmoothingConfigs,
    derivedValues: derivedValuesWithComputed,
    setDerivedValues,
    regressionConfigs,
    setRegressionConfigs,
    regressionResults,
    setRegressionResults,
    localThresholdOverrides,
    setLocalThresholdOverrides,
    sensorVisibilityConfig,
    setSensorVisibilityConfig,
    dateMode,
    setDateMode,
    exitLiveTail,
    customSensorColors,
    setCustomSensorColors,
    cadenceSeconds,
    setCadenceSeconds,

    // Computed values
    unitMapping,
    computedScalePairs,

    // Functions
    fetchRawData,
    prefetchTimeRange,
    handleRemoveDerivedValue,
    createDerivedValues,
    handleChangeColor,
    loadTriggerRef,
  }
}
